import { useEffect, useMemo, useRef, useState } from 'react';
import { fmtUSD, fmtUSDc } from '../lib/format';

interface Point {
  label: string;
  amountCents: number;
}

interface Props {
  data: Point[];
  height?: number;
}

/** Round `range` to a 1/2/5/10 × 10ⁿ "nice" number for axis ticks. */
function niceNum(range: number, round: boolean): number {
  if (range <= 0) return 1;
  const exp = Math.floor(Math.log10(range));
  const frac = range / Math.pow(10, exp);
  let nf: number;
  if (round) {
    nf = frac < 1.5 ? 1 : frac < 3 ? 2 : frac < 7 ? 5 : 10;
  } else {
    nf = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  }
  return nf * Math.pow(10, exp);
}

/**
 * Auto-scale the Y axis to the data's own peak (values in cents). Baseline
 * stays at 0 so the area fill reads honestly; the top is a nice round number
 * just above the highest month. Works at any magnitude — a $60/mo creator and
 * a $60k/mo creator both fill the chart's height.
 */
function niceScale(maxCents: number, tickTarget: number): { niceMax: number; step: number } {
  if (maxCents <= 0) return { niceMax: 100, step: 25 }; // no earnings yet → $0–$1
  const step = Math.max(1, niceNum(maxCents / tickTarget, true));
  const niceMax = Math.max(step, Math.ceil(maxCents / step) * step);
  return { niceMax, step };
}

/**
 * Inline SVG chart ported from the prototype. We keep the implementation
 * (smooth bezier path + dashed gridlines + tooltip on hover) rather than
 * pulling in Recharts — it's ~120 lines and avoids a heavy dependency.
 */
export function EarningsChart({ data, height = 240 }: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(800);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(Math.max(320, e.contentRect.width));
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const padL = 44;
  const padR = 14;
  const padT = 14;
  const padB = 28;
  const innerW = w - padL - padR;
  const innerH = height - padT - padB;

  const maxC = data.length ? Math.max(0, ...data.map((d) => d.amountCents)) : 0;
  const { niceMax, step } = niceScale(maxC, 4);
  const niceMin = 0;

  const xs = data.map((_, i) => padL + (innerW * i) / Math.max(1, data.length - 1));
  const ys = data.map((d) =>
    padT + innerH - (innerH * (d.amountCents - niceMin)) / (niceMax - niceMin || 1),
  );

  const tickCount = Math.max(1, Math.round((niceMax - niceMin) / step));
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => niceMin + step * i);
  // Adaptive label: $k for ≥$1000 axes, cents for sub-$1 steps, whole $ otherwise.
  const fmtTick = (cents: number): string => {
    if (niceMax >= 100_000) {
      const k = cents / 100_000;
      return '$' + (Number.isInteger(k) ? k : k.toFixed(1)) + 'k';
    }
    return step < 100 ? fmtUSD(cents) : fmtUSDc(cents);
  };

  const path = useMemo(() => {
    if (xs.length < 2) return '';
    let p = `M ${xs[0]} ${ys[0]}`;
    for (let i = 1; i < xs.length; i++) {
      const cx1 = xs[i - 1] + (xs[i] - xs[i - 1]) * 0.4;
      const cx2 = xs[i] - (xs[i] - xs[i - 1]) * 0.4;
      p += ` C ${cx1} ${ys[i - 1]}, ${cx2} ${ys[i]}, ${xs[i]} ${ys[i]}`;
    }
    return p;
  }, [xs, ys]);
  const areaPath = xs.length > 0 ? path + ` L ${xs[xs.length - 1]} ${padT + innerH} L ${xs[0]} ${padT + innerH} Z` : '';

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    const mx = e.clientX - rect.left;
    let closest = 0;
    let best = Infinity;
    xs.forEach((x, i) => {
      const d = Math.abs(x - mx);
      if (d < best) {
        best = d;
        closest = i;
      }
    });
    setHover(closest);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <svg width={w} height={height} style={{ display: 'block' }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id="earningsArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--brand)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--brand)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {ticks.map((t, i) => {
          const y = padT + innerH - (innerH * (t - niceMin)) / (niceMax - niceMin || 1);
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="var(--line)" strokeDasharray={i === 0 ? '0' : '2 4'} />
              <text x={padL - 8} y={y + 3} fontSize="10" fill="var(--ink-3)" textAnchor="end" fontFamily="var(--font-mono)">
                {fmtTick(t)}
              </text>
            </g>
          );
        })}
        {areaPath && <path d={areaPath} fill="url(#earningsArea)" />}
        {path && <path d={path} fill="none" stroke="var(--brand)" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />}
        {data.map(
          (d, i) =>
            (i % 2 === 0 || i === data.length - 1) && (
              <text key={i} x={xs[i]} y={height - 8} fontSize="10" textAnchor="middle" fill="var(--ink-3)" fontFamily="var(--font-mono)">
                {d.label}
              </text>
            ),
        )}
        {hover != null && data[hover] && (
          <g>
            <line x1={xs[hover]} y1={padT} x2={xs[hover]} y2={padT + innerH} stroke="var(--ink-4)" strokeDasharray="2 3" />
            <circle cx={xs[hover]} cy={ys[hover]} r="5" fill="var(--bg-elev)" stroke="var(--brand)" strokeWidth="2.25" />
          </g>
        )}
      </svg>
      {hover != null && data[hover] && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(w - 140, Math.max(0, xs[hover] - 60)),
            top: ys[hover] - 56,
            background: 'var(--ink)',
            color: 'var(--bg-elev)',
            padding: '8px 10px',
            borderRadius: 8,
            fontSize: 12,
            pointerEvents: 'none',
            boxShadow: 'var(--shadow-elev)',
            whiteSpace: 'nowrap',
          }}
        >
          <div style={{ opacity: 0.6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {data[hover].label}
          </div>
          <div className="num" style={{ fontWeight: 600, fontSize: 14 }}>
            {fmtUSD(data[hover].amountCents)}
          </div>
        </div>
      )}
    </div>
  );
}
